import React, { useRef, useState } from 'react'
import { useRouter } from 'expo-router'
import { useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { useStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'
import { useOnTransitionToTrue } from '@/hooks/useOnTransitionToTrue'
import { TournamentWinnerModal } from './TournamentWinnerModal'
import { redirectToSummary } from './mttNavigation'

interface Props {
  tournamentId: Id<'tournaments'>
  /** True when arriving via the in-game game-over modal (`?justFinished=1`),
   * which already announced the winner — don't announce twice. */
  skipAnnouncement: boolean
}

/**
 * Winner announcement for users who are in the lobby (not at a table) when
 * the tournament finishes — the in-game `TournamentGameOverModal` only covers
 * users on a `/game/[gameId]` route. Self-gates on the server status:
 * renders nothing until the tournament is finished with a recorded winner.
 *
 * Shows once per layout mount: dismissing (Close / View Results / hardware
 * back) keeps it down for the rest of the mounted session, but a fresh lobby
 * visit to a finished tournament announces again — deliberate, so a winner
 * opening the results from MyTournaments still gets the celebration.
 */
export function TournamentWinnerPrompt({ tournamentId, skipAnnouncement }: Props) {
  const router = useRouter()
  const { setEvent } = useStore().analytics
  const userId = useStore().session.user?.auth0Id

  const status = useQuery(api.tournaments.queries.getTournamentStatus, { tournamentId })
  const [dismissed, setDismissed] = useState(false)
  // Re-arm if the user navigates to a different tournament's lobby.
  const armedForRef = useRef<string | null>(null)
  if (armedForRef.current !== tournamentId) {
    armedForRef.current = tournamentId
    if (dismissed) setDismissed(false)
  }

  const suppress = skipAnnouncement || dismissed
  const shouldAnnounce = !suppress && status?.state === 'finished' && !!status.winnerName

  useOnTransitionToTrue(shouldAnnounce, () => {
    setEvent(events.mtt.winner_modal_viewed, { tournamentId })
  })

  return (
    <TournamentWinnerModal
      visible={shouldAnnounce}
      winnerName={status?.winnerName ?? null}
      isWinner={!!userId && status?.winnerId === userId}
      tournamentName={status?.name}
      onViewResults={() => {
        setDismissed(true)
        redirectToSummary(router, tournamentId)
      }}
      onClose={() => setDismissed(true)}
    />
  )
}
